/**
 * useChartWidth — measures the chart container so every SVG can draw at 1 unit = 1 CSS px
 * (the mockup's `fitCharts()`), keeping 11px axis text 11px on a phone and on a desktop grid.
 */

import { useEffect, useRef, useState } from 'react';

export function useChartWidth(fallback = 720): [(node: HTMLDivElement | null) => void, number] {
  const [width, setWidth] = useState(fallback);
  const observerRef = useRef<ResizeObserver | null>(null);
  const nodeRef = useRef<HTMLDivElement | null>(null);

  useEffect(
    () => () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    },
    [],
  );

  const ref = (node: HTMLDivElement | null) => {
    if (nodeRef.current === node) return;
    observerRef.current?.disconnect();
    nodeRef.current = node;
    if (!node) return;
    const measure = () => {
      const next = Math.round(node.getBoundingClientRect().width);
      if (next > 60) setWidth((prev) => (Math.abs(prev - next) > 1 ? next : prev));
    };
    measure();
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(measure);
      observer.observe(node);
      observerRef.current = observer;
    }
  };

  return [ref, width];
}
